import { AlertTriangle, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { GoalFlowStory } from "@/components/cinch/GoalFlowStory";
import { Button } from "@/components/ui/button";
import { useCan } from "@/lib/auth";

import { useGoalsOverview, type GoalSummary } from "../api";
import { GoalCard } from "../components/GoalCard";
import { GoalDetail } from "../components/GoalDetail";
import { GoalForm } from "../components/GoalForm";
import { buildStoryTiers } from "../stories/2026";

/**
 * Company Goals — opens with the year's "Make It Easy" narrative flow (the hero
 * GoalFlowStory). Clicking a flow box or a goal card opens the goal detail in a
 * drawer (description, success criteria, linked objectives). Write users get
 * New / Edit; AI insights arrives in Phase 4.
 */
export function GoalsPage() {
  const canWrite = useCan("goals.write");
  const { data, isFetching, error } = useGoalsOverview();

  // Drawer state (selected goal) + form state. The drawer is non-modal so the
  // sidebar stays clickable; we own open/expanded per EntityDrawer's contract.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<GoalSummary | null>(null);

  const tiers = useMemo(() => (data ? buildStoryTiers(data.year, data.goals) : []), [data]);
  const selectedGoal = data?.goals.find((g) => g.id === selectedId) ?? null;

  function closeDrawer() {
    setSelectedId(null);
    setExpanded(false);
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Company Goals{data ? ` · ${data.year}` : ""}
          </div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-foreground">
            {data?.headline ?? "Company Goals"}
          </h1>
          {data?.subhead ? (
            <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">{data.subhead}</p>
          ) : null}
        </div>
        {canWrite ? (
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            disabled={!data}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New goal
          </Button>
        ) : null}
      </header>

      {error != null ? (
        <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger-light/60 px-4 py-2.5 text-xs font-semibold text-danger">
          <AlertTriangle className="mt-px h-4 w-4 flex-shrink-0" />
          <span>Goals unavailable: {error instanceof Error ? error.message : String(error)}</span>
        </div>
      ) : null}

      {!data && isFetching ? (
        <p className="text-sm text-muted-foreground">Loading goals…</p>
      ) : null}

      {data ? (
        <>
          {tiers.length > 0 ? (
            <section className="rounded-xl border border-border bg-card p-5 shadow-sm sm:p-6">
              <GoalFlowStory tiers={tiers} onSelect={setSelectedId} selectedId={selectedId} />
            </section>
          ) : null}

          {data.goals.length > 0 ? (
            <section>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                The {data.goals.length} goals
              </h2>
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
                {data.goals.map((g) => (
                  <GoalCard
                    key={g.id}
                    goal={g}
                    selected={g.id === selectedId}
                    onClick={() => setSelectedId(g.id)}
                  />
                ))}
              </div>
            </section>
          ) : (
            <p className="text-sm text-muted-foreground">
              No goals defined for {data.year} yet.
              {canWrite ? " Use “New goal” to add the first one." : ""}
            </p>
          )}
        </>
      ) : null}

      <EntityDrawer
        open={selectedId != null}
        onOpenChange={(o) => {
          if (!o) closeDrawer();
        }}
        expanded={expanded}
        onExpandedChange={setExpanded}
        title={selectedGoal?.title ?? "Goal"}
        subtitle={
          selectedGoal
            ? `${selectedGoal.year}${selectedGoal.short_label ? ` · ${selectedGoal.short_label}` : ""}`
            : undefined
        }
        onClose={closeDrawer}
      >
        {selectedId ? (
          <GoalDetail
            goalId={selectedId}
            canWrite={canWrite}
            openwebuiUrl={data?.openwebui_url ?? null}
            onEdit={() => {
              if (selectedGoal) {
                setEditing(selectedGoal);
                setFormOpen(true);
              }
            }}
          />
        ) : null}
      </EntityDrawer>

      {formOpen && data ? (
        <GoalForm
          year={data.year}
          goal={editing}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}
